import PropTypes from 'prop-types';

import HeatmapLineDashboard from './HeatmapLineDashboard';
import { useState } from 'react';
import SharedButton from '@/global/Button/Button';
import { getAvailableChartsByContext } from '@/modules/search';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { PresentationChartLineIcon } from '@heroicons/react/24/outline';

interface GraphTopContentProps {
  graphSearchVariables: any;
  chartInfo: any;
  setChartInfo: any;
  parent: any;
  resultsNumber: any;
  totalResults?: any;
  selectedSampleSize: any;
  onSampleSizeChange: any;
  sampleSizeOptions: any;
  names?: any[];
  view?: string;
}

function GraphTitle({ parent, resultsNumber }) {
  return (
    <h3 className="">
      Top {resultsNumber} {parent} results
    </h3>
  );
}

// Define prop types for GraphTitle
GraphTitle.propTypes = {
  chartVariable: PropTypes.string.isRequired,
  parent: PropTypes.string.isRequired,
  resultsNumber: PropTypes.number.isRequired,
  totalResults: PropTypes.number,
};

function GraphTopContent({
  graphSearchVariables,
  chartInfo,
  setChartInfo,
  parent,
  resultsNumber,
  selectedSampleSize,
  onSampleSizeChange,
  sampleSizeOptions,
  names = [],
  view = '',
}: GraphTopContentProps) {
  const [chartsSelect, setChartsSelect] = useState(false);
  const selectedChart = chartInfo?.type || 'LINE';

  const handleDropdownClick = (item: string) => {
    const nextChartType = item.toUpperCase();
    setChartInfo((prevChartInfo: any) => ({ ...prevChartInfo, type: nextChartType }));
    setChartsSelect(false);
  };
  const availableCharts = getAvailableChartsByContext(names, parent, view);

  const renderChartIcon = () => {
    if (selectedChart === 'HEATMAP') {
      return <img src="/static/images/icons/heatmapIcon.svg" alt="Heatmap" className="w-4 h-4 mr-2" />;
    } else if (selectedChart === 'LINE') {
      return <PresentationChartLineIcon className="w-4 h-4 mr-2" />;
    }
    return <img src="/static/images/icons/chartIcon.svg" alt="Line" className="w-4 h-4 mr-2" />;
  };

  return (
    <div className="graph-top-content">
      <div className="row items-center justify-between">
        {resultsNumber !== 0 && <GraphTitle parent={parent} resultsNumber={resultsNumber} />}
        <div className="relative inline-block">
          <SharedButton
            variant="secondary"
            onClick={() => setChartsSelect(!chartsSelect)}
            iconRight={
              !chartsSelect ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />
            }
            className="mb-0"
          >
            {renderChartIcon()}
            {selectedChart}
          </SharedButton>
          {/* Dropdown */}
          {chartsSelect && (
            <div className="absolute left-0 top-full z-10 mt-1 w-full rounded-md shadow-lg bg-white border">
              <ul className="py-1 text-sm text-gray-700">
                {availableCharts.map((item, idx) => (
                  <li
                    key={idx}
                    className="px-4 py-2 hover:bg-blue cursor-pointer hover:text-white"
                    onClick={() => handleDropdownClick(item)}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="row items-center justify-between">
        <HeatmapLineDashboard
          chartVariables={graphSearchVariables}
          chartInfo={chartInfo}
          setChartInfo={setChartInfo}
        />
      </div>
      {onSampleSizeChange && (
        <div className="graph-sample-selector">
          <label htmlFor={`${parent}-sample-size`}>Show results:</label>
          <select
            id={`${parent}-sample-size`}
            value={selectedSampleSize}
            onChange={event => {
              const value = event.target.value;
              onSampleSizeChange(value === 'ALL' ? 'ALL' : Number(value));
            }}
          >
            {sampleSizeOptions.map(optionValue => (
              <option key={optionValue} value={optionValue}>
                {optionValue === 'ALL' ? 'All' : optionValue}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// Define prop types for GraphTopContent
GraphTopContent.propTypes = {
  graphSearchVariables: PropTypes.array.isRequired, // Adjust the prop type accordingly
  chartInfo: PropTypes.object.isRequired,
  setChartInfo: PropTypes.func.isRequired,
  parent: PropTypes.string.isRequired,
  resultsNumber: PropTypes.number.isRequired,
  totalResults: PropTypes.number,
  selectedSampleSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSampleSizeChange: PropTypes.func,
  sampleSizeOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  names: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  view: PropTypes.string,
};

export default GraphTopContent;
